import { useParams, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { SectionRenderer } from '@/components/sections/SectionRenderer';
import { LoadingLabel, Skel, SkelLines } from '@/components/home/TenantSkeletons';
import { useLayoutStyle } from '@/contexts/ThemeContext';
import { useTenantOrgId } from '@/hooks/useTenantOrgId';
import { PageSeo } from '@/components/seo/PageSeo';
import { useCustomPage } from '@/hooks/useCustomPage';

const CustomPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const orgId = useTenantOrgId();
  const layoutStyle = useLayoutStyle();

  const { data, isLoading, isError } = useCustomPage(orgId, slug);

  // While orgId/slug are not yet resolved, or while the query is in flight, show
  // the spinner — same behaviour as the original useEffect approach.
  if (!orgId || !slug || isLoading) {
    return (
      <Layout>
        <section className="tp-section min-h-[60vh]" aria-busy="true">
          <LoadingLabel>Loading page</LoadingLabel>
          <div className="tp-skeleton container mx-auto px-5 md:px-6" aria-hidden>
            <div className="mx-auto max-w-3xl text-center">
              <Skel className="mx-auto h-3 w-24 tp-skel-round" />
              <div className="tp-h2 mt-6"><SkelLines lines={2} widths={['86%', '52%']} className="flex flex-col items-center" /></div>
              <div className="tp-lead mt-6"><SkelLines lines={3} widths={['100%', '94%', '60%']} className="flex flex-col items-center" /></div>
            </div>
            <Skel className="tp-skel-media mx-auto mt-14 aspect-[16/7] w-full max-w-5xl" />
          </div>
        </section>
      </Layout>
    );
  }

  // null data means the page was not found (query succeeded but returned nothing).
  // isError covers network/DB errors — treat both as not-found, identical to
  // the original `setNotFound(true)` path.
  if (isError || data === null) {
    return <Navigate to="/404" replace />;
  }

  const { page, sections } = data;

  return (
    <Layout>
      <PageSeo
        pageSlug={page.slug}
        fallbackTitle={page.title}
        fallbackDescription={page.meta_description ?? undefined}
      />
      {sections.length === 0 ? (
        <div className="min-h-[60vh] flex items-center justify-center">
          <p className="text-muted-foreground">This page has no content yet.</p>
        </div>
      ) : (
        sections.map((section) => (
          <SectionRenderer
            key={section.id}
            section={section}
            layoutStyle={layoutStyle}
          />
        ))
      )}
    </Layout>
  );
};

export default CustomPage;
